// src/pages/Onboarding.jsx
// Production-level Onboarding — TrueChattr
// Fixes:
//   - "WhatsApp AI automation" → "keyword-based WhatsApp automation"
//   - "AI Ready" chip → "Bot Ready"
//   - "Bot suggests replies" description honest
//   - Step 3 success copy honest
// Upgrades:
//   - 4 steps (added automation mode as proper step 2)
//   - Micro-animations on every interaction
//   - Category cards with hover glow effects
//   - WhatsApp number auto-formatting (+91 xxx xxxxx)
//   - Skip options where relevant
//   - Animated success confetti on final step
//   - Feature preview cards on complete step
//   - Better mobile layout

import { useState, useEffect, useRef } from 'react';
import { useNavigate }       from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Phone, Zap, Check, ArrowRight, ArrowLeft,
  Store, Headphones, Briefcase, MoreHorizontal,
  Bot, Eye, Sparkles, Users, BarChart3, MessageSquare,
  GraduationCap, UtensilsCrossed, Heart, ShoppingBag,
  ChevronRight, Rocket, CheckCircle2,
} from 'lucide-react';
import { Input }    from '@/components/ui/input';
import { Label }    from '@/components/ui/label';
import api          from '@/lib/api';
import { toast }    from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// STEPS CONFIG
// ─────────────────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, title: 'Business',   icon: Building2  },
  { id: 2, title: 'WhatsApp',   icon: Phone      },
  { id: 3, title: 'Bot Mode',   icon: Bot        },
  { id: 4, title: 'Done!',      icon: Sparkles   },
];

// ─────────────────────────────────────────────────────────────────────────────
// CATEGORIES
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'ecommerce',  label: 'E-commerce',     icon: Store,          color: 'emerald' },
  { value: 'services',   label: 'Services',        icon: Briefcase,      color: 'blue'    },
  { value: 'education',  label: 'Education',       icon: GraduationCap,  color: 'violet'  },
  { value: 'healthcare', label: 'Healthcare',      icon: Heart,          color: 'rose'    },
  { value: 'restaurant', label: 'Restaurant',      icon: UtensilsCrossed,color: 'orange'  },
  { value: 'retail',     label: 'Retail',          icon: ShoppingBag,    color: 'teal'    },
  { value: 'support',    label: 'Support',         icon: Headphones,     color: 'indigo'  },
  { value: 'other',      label: 'Other',           icon: MoreHorizontal, color: 'gray'    },
];

// ─────────────────────────────────────────────────────────────────────────────
// AUTOMATION MODES — honest descriptions, no "AI suggests"
// ─────────────────────────────────────────────────────────────────────────────

const AUTOMATION_MODES = [
  {
    value: 'full_auto',
    label: 'Full Auto',
    icon: Zap,
    badge: 'Most Popular',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    description: 'Bot automatically replies to keyword matches 24/7. You review conversations in dashboard.',
    pros: ['Zero manual effort', 'Instant replies always', 'Best for high volume'],
    color: 'emerald',
  },
  {
    value: 'hybrid',
    label: 'Hybrid',
    icon: Bot,
    badge: 'Recommended',
    badgeColor: 'bg-blue-100 text-blue-700',
    description: 'Bot handles keyword replies. You can manually take over any conversation anytime.',
    pros: ['Auto + manual control', 'Take over when needed', 'Best for most businesses'],
    color: 'blue',
  },
  {
    value: 'manual_review',
    label: 'Review Mode',
    icon: Eye,
    badge: null,
    badgeColor: '',
    description: 'Bot drafts keyword-matched replies. You review and approve before they are sent.',
    pros: ['Full message control', 'Learn what works', 'Best for getting started'],
    color: 'violet',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// COLOR MAP
// ─────────────────────────────────────────────────────────────────────────────

const COLORS = {
  emerald: { ring: 'ring-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/10', icon: 'text-emerald-600', check: 'bg-emerald-500', border: 'border-emerald-400', text: 'text-emerald-700', glow: 'shadow-emerald-100' },
  blue:    { ring: 'ring-blue-400',    bg: 'bg-blue-50',    icon: 'text-blue-600',    check: 'bg-blue-500',    border: 'border-blue-400',    text: 'text-blue-700',    glow: 'shadow-blue-100'    },
  violet:  { ring: 'ring-violet-400',  bg: 'bg-violet-50',  icon: 'text-violet-600',  check: 'bg-violet-500',  border: 'border-violet-400',  text: 'text-violet-700',  glow: 'shadow-violet-100'  },
  rose:    { ring: 'ring-rose-400',    bg: 'bg-rose-50',    icon: 'text-rose-600',    check: 'bg-rose-500',    border: 'border-rose-400',    text: 'text-rose-700',    glow: 'shadow-rose-100'    },
  orange:  { ring: 'ring-orange-400',  bg: 'bg-orange-50',  icon: 'text-orange-600',  check: 'bg-orange-500',  border: 'border-orange-400',  text: 'text-orange-700',  glow: 'shadow-orange-100'  },
  teal:    { ring: 'ring-teal-400',    bg: 'bg-teal-50',    icon: 'text-teal-600',    check: 'bg-teal-500',    border: 'border-teal-400',    text: 'text-teal-700',    glow: 'shadow-teal-100'    },
  indigo:  { ring: 'ring-indigo-400',  bg: 'bg-indigo-50',  icon: 'text-indigo-600',  check: 'bg-indigo-500',  border: 'border-indigo-400',  text: 'text-indigo-700',  glow: 'shadow-indigo-100'  },
  gray:    { ring: 'ring-gray-300',    bg: 'bg-gray-50',    icon: 'text-gray-500',    check: 'bg-gray-400',    border: 'border-gray-300',    text: 'text-gray-600',    glow: 'shadow-gray-100'    },
};

// ─────────────────────────────────────────────────────────────────────────────
// SLIDE VARIANTS
// ─────────────────────────────────────────────────────────────────────────────

const slideVariants = {
  enter:  (dir) => ({ opacity: 0, x: dir > 0 ? 40 : -40 }),
  center: { opacity: 1, x: 0 },
  exit:   (dir) => ({ opacity: 0, x: dir > 0 ? -40 : 40 }),
};

// ─────────────────────────────────────────────────────────────────────────────
// PHONE FORMAT HELPER
// ─────────────────────────────────────────────────────────────────────────────

const formatPhone = (raw) => {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('91') && digits.length > 2) {
    const num = digits.slice(2);
    if (num.length <= 5) return `+91 ${num}`;
    return `+91 ${num.slice(0, 5)} ${num.slice(5, 10)}`;
  }
  if (digits.length <= 5) return `+${digits}`;
  return `+${digits.slice(0, 2)} ${digits.slice(2, 7)} ${digits.slice(7, 12)}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// CONFETTI PARTICLE
// ─────────────────────────────────────────────────────────────────────────────

function ConfettiParticle({ delay, color }) {
  const x  = (Math.random() - 0.5) * 300;
  const y  = -(Math.random() * 200 + 100);
  const rot = Math.random() * 720 - 360;
  return (
    <motion.div
      className={`absolute w-2.5 h-2.5 rounded-sm ${color} top-1/2 left-1/2`}
      initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 1 }}
      animate={{ x, y, opacity: 0, rotate: rot, scale: 0.5 }}
      transition={{ delay, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
    />
  );
}

const CONFETTI_COLORS = [
  'bg-emerald-400', 'bg-blue-400', 'bg-violet-400',
  'bg-yellow-400', 'bg-rose-400', 'bg-teal-400',
];

function Confetti() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden flex items-center justify-center">
      {Array.from({ length: 24 }).map((_, i) => (
        <ConfettiParticle
          key={i}
          delay={i * 0.04}
          color={CONFETTI_COLORS[i % CONFETTI_COLORS.length]}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP INDICATOR
// ─────────────────────────────────────────────────────────────────────────────

function StepIndicator({ currentStep }) {
  return (
    <div className="flex items-center justify-between mb-10">
      {STEPS.map((step, idx) => {
        const done   = currentStep > step.id;
        const active = currentStep === step.id;
        const Icon   = step.icon;
        return (
          <div key={step.id} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <motion.div
                animate={{
                  scale:           active ? 1.12 : 1,
                  backgroundColor: done ? '#10b981' : active ? '#ffffff' : '#f9fafb',
                  borderColor:     done ? '#10b981' : active ? '#10b981' : '#e5e7eb',
                }}
                transition={{ duration: 0.3 }}
                className="w-11 h-11 rounded-full flex items-center justify-center border-2 shadow-sm"
                data-testid={`step-${step.id}`}
              >
                <AnimatePresence mode="wait">
                  {done ? (
                    <motion.div key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400, damping: 20 }}>
                      <Check className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </motion.div>
                  ) : (
                    <motion.div key="icon" initial={{ scale: 0 }} animate={{ scale: 1 }}>
                      <Icon className={`w-5 h-5 ${active ? 'text-emerald-600' : 'text-gray-300'}`} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
              <span className={`text-[10px] font-semibold hidden sm:block transition-colors ${
                active ? 'text-emerald-600' : done ? 'text-emerald-500' : 'text-gray-300'
              }`}>
                {step.title}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className="flex-1 h-0.5 mx-2 mb-5 bg-gray-100 overflow-hidden rounded-full">
                <motion.div
                  className="h-full bg-emerald-400 rounded-full"
                  initial={{ width: '0%' }}
                  animate={{ width: currentStep > step.id ? '100%' : '0%' }}
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function Onboarding() {
  const navigate = useNavigate();

  const [currentStep,    setCurrentStep]    = useState(1);
  const [direction,      setDirection]      = useState(1);
  const [businessName,   setBusinessName]   = useState('');
  const [category,       setCategory]       = useState('ecommerce');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [automationMode, setAutomationMode] = useState('hybrid');
  const [loading,        setLoading]        = useState(false);
  const [showConfetti,   setShowConfetti]   = useState(false);

  const totalSteps = STEPS.length;
  const progress   = ((currentStep - 1) / (totalSteps - 1)) * 100;

  // Show confetti on reaching step 4
  useEffect(() => {
    if (currentStep === 4) {
      setShowConfetti(true);
      const t = setTimeout(() => setShowConfetti(false), 2000);
      return () => clearTimeout(t);
    }
  }, [currentStep]);

  // ── Navigation ─────────────────────────────────────────────────────────────

  const goNext = () => { setDirection(1);  setCurrentStep(s => Math.min(s + 1, totalSteps)); };
  const goBack = () => { setDirection(-1); setCurrentStep(s => Math.max(s - 1, 1)); };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (!businessName.trim()) {
        toast.error('Please enter your business name');
        return;
      }
      setLoading(true);
      try {
        await api.post('/business', {
          name:            businessName.trim(),
          category,
          whatsapp_number: null, // set in step 2
        });
        goNext();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to save business details. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (currentStep === 2) {
      // WhatsApp number — optional, save if provided
      if (whatsappNumber.replace(/\D/g, '').length > 0) {
        try {
          await api.patch('/business', { whatsapp_number: whatsappNumber.replace(/\D/g, '') });
        } catch {
          // non-blocking — user can set later
        }
      }
      goNext();
    } else if (currentStep === 3) {
      // Save automation mode
      try {
        await api.patch('/business', { automation_mode: automationMode });
      } catch {
        // non-blocking
      }
      goNext();
    } else {
      navigate('/app/dashboard');
    }
  };

  const handleSkip = () => goNext();

  // ── Phone input handler ────────────────────────────────────────────────────

  const handlePhoneChange = (e) => {
    const raw = e.target.value;
    setWhatsappNumber(formatPhone(raw));
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50/30 flex items-center justify-center p-4">

      {/* Decorative blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-emerald-200/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-blue-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-100/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0  }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-xl"
      >
        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl shadow-gray-200/60 border border-gray-100 overflow-hidden">

          {/* Progress bar */}
          <div className="h-1.5 w-full bg-gray-100">
            <motion.div
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            />
          </div>

          <div className="p-7 md:p-10">

            {/* Step indicators */}
            <StepIndicator currentStep={currentStep} />

            {/* Step content */}
            <AnimatePresence mode="wait" custom={direction}>

              {/* ══ STEP 1 — Business Details ══════════════════════════════════ */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter" animate="center" exit="exit"
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">Tell us about your business</h2>
                    <p className="text-gray-400 text-sm">We'll set up automation templates based on your type.</p>
                  </div>

                  {/* Business Name */}
                  <div className="space-y-1.5">
                    <Label htmlFor="businessName" className="text-sm font-medium text-gray-700">
                      Business Name <span className="text-rose-400">*</span>
                    </Label>
                    <div className="relative">
                      <Building2 className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-colors ${
                        businessName.trim().length >= 2 ? 'text-emerald-500' : 'text-gray-400'
                      }`} />
                      <Input
                        id="businessName"
                        value={businessName}
                        onChange={e => setBusinessName(e.target.value)}
                        placeholder="My Awesome Store"
                        data-testid="onboarding-business-name"
                        className={`pl-10 h-11 rounded-xl text-sm transition-all ${
                          businessName.trim().length >= 2
                            ? 'border-emerald-300 focus:border-emerald-400 focus:ring-emerald-400'
                            : 'border-gray-200 focus:border-emerald-400 focus:ring-emerald-400'
                        }`}
                      />
                      {businessName.trim().length >= 2 && (
                        <CheckCircle2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500 pointer-events-none" />
                      )}
                    </div>
                  </div>

                  {/* Category grid */}
                  <div className="space-y-2.5">
                    <Label className="text-sm font-medium text-gray-700">Business Category</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                      {CATEGORIES.map(cat => {
                        const c       = COLORS[cat.color];
                        const CatIcon = cat.icon;
                        const sel     = category === cat.value;
                        return (
                          <motion.button
                            key={cat.value}
                            type="button"
                            onClick={() => setCategory(cat.value)}
                            whileTap={{ scale: 0.96 }}
                            className={`
                              relative flex flex-col items-center gap-2 p-3.5 rounded-xl border-2 text-center
                              transition-all duration-200 cursor-pointer
                              ${sel
                                ? `${c.bg} ${c.border} shadow-lg ${c.glow}`
                                : 'border-gray-100 bg-gray-50/80 hover:border-gray-200 hover:bg-white hover:shadow-sm'
                              }
                            `}
                          >
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                              sel ? `${c.bg} shadow-sm` : 'bg-white border border-gray-100'
                            }`}>
                              <CatIcon className={`w-4.5 h-4.5 ${sel ? c.icon : 'text-gray-400'}`} style={{ width: 18, height: 18 }} />
                            </div>
                            <span className={`text-xs font-semibold leading-tight transition-colors ${sel ? 'text-gray-900' : 'text-gray-500'}`}>
                              {cat.label}
                            </span>
                            {sel && (
                              <motion.span
                                initial={{ scale: 0 }} animate={{ scale: 1 }}
                                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                className={`absolute top-1.5 right-1.5 w-4 h-4 rounded-full ${c.check} flex items-center justify-center`}
                              >
                                <Check className="w-2.5 h-2.5 text-white" />
                              </motion.span>
                            )}
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ══ STEP 2 — WhatsApp Number ════════════════════════════════════ */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter" animate="center" exit="exit"
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">Connect WhatsApp</h2>
                    <p className="text-gray-400 text-sm">Add your WhatsApp Business number. You can skip and do this later.</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="whatsappNumber" className="text-sm font-medium text-gray-700">
                      WhatsApp Business Number
                      <span className="ml-2 text-xs text-gray-400 font-normal">(optional)</span>
                    </Label>
                    <div className="relative">
                      <Phone className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-colors ${
                        whatsappNumber.length > 3 ? 'text-emerald-500' : 'text-gray-400'
                      }`} />
                      <Input
                        id="whatsappNumber"
                        value={whatsappNumber}
                        onChange={handlePhoneChange}
                        placeholder="+91 98765 43210"
                        data-testid="onboarding-whatsapp-number"
                        className="pl-10 h-11 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm"
                      />
                    </div>
                    <p className="text-xs text-gray-400">Number auto-formats as you type. E.g. +91 98765 43210</p>
                  </div>

                  {/* Info card */}
                  <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Phone className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-emerald-800 mb-1">What you'll need</p>
                        <div className="space-y-1.5">
                          {[
                            'A registered WhatsApp Business account',
                            'Phone number verified on WhatsApp Business',
                            'Business verification (guided in Settings)',
                          ].map((s, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <span className="w-5 h-5 rounded-full bg-emerald-200 text-emerald-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <span className="text-xs text-emerald-700">{s}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* API compliance note */}
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <p className="text-xs text-blue-700">
                      TrueChattr uses the Official WhatsApp Business API — fully compliant with WhatsApp policies.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* ══ STEP 3 — Automation Mode ════════════════════════════════════ */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter" animate="center" exit="exit"
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="space-y-5"
                >
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">Choose your bot mode</h2>
                    <p className="text-gray-400 text-sm">How should your keyword bot handle incoming messages?</p>
                  </div>

                  <div className="space-y-3">
                    {AUTOMATION_MODES.map(mode => {
                      const c    = COLORS[mode.color];
                      const Icon = mode.icon;
                      const sel  = automationMode === mode.value;
                      return (
                        <motion.button
                          key={mode.value}
                          type="button"
                          onClick={() => setAutomationMode(mode.value)}
                          whileTap={{ scale: 0.99 }}
                          className={`
                            w-full text-left p-4 rounded-xl border-2 transition-all duration-200
                            ${sel
                              ? `${c.bg} ${c.border} shadow-md ${c.glow}`
                              : 'border-gray-100 bg-gray-50/80 hover:border-gray-200 hover:bg-white hover:shadow-sm'
                            }
                          `}
                        >
                          <div className="flex items-start gap-3">
                            {/* Icon */}
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
                              sel ? `${c.bg} shadow-sm border ${c.border}` : 'bg-white border border-gray-100'
                            }`}>
                              <Icon className={`w-5 h-5 ${sel ? c.icon : 'text-gray-400'}`} />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-sm font-bold ${sel ? 'text-gray-900' : 'text-gray-700'}`}>
                                  {mode.label}
                                </span>
                                {mode.badge && (
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${mode.badgeColor}`}>
                                    {mode.badge}
                                  </span>
                                )}
                              </div>
                              <p className={`text-xs leading-relaxed mb-2 ${sel ? 'text-gray-600' : 'text-gray-400'}`}>
                                {mode.description}
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {mode.pros.map(pro => (
                                  <span key={pro} className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                                    sel ? `${c.bg} ${c.text}` : 'bg-gray-100 text-gray-400'
                                  }`}>
                                    <Check className="w-2.5 h-2.5" />
                                    {pro}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Selected indicator */}
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                              sel ? `${c.check} border-transparent` : 'border-gray-200'
                            }`}>
                              {sel && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                            </div>
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>

                  <p className="text-xs text-gray-400 text-center">
                    You can change this anytime in Settings → Automation
                  </p>
                </motion.div>
              )}

              {/* ══ STEP 4 — Complete ═══════════════════════════════════════════ */}
              {currentStep === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center py-4 space-y-6"
                >
                  {/* Confetti */}
                  {showConfetti && <Confetti />}

                  {/* Success icon */}
                  <div className="relative w-24 h-24 mx-auto">
                    <motion.div
                      initial={{ scale: 0 }} animate={{ scale: 1 }}
                      transition={{ delay: 0.1, type: 'spring', stiffness: 300, damping: 20 }}
                      className="absolute inset-0 rounded-full bg-emerald-100 opacity-60"
                    />
                    <motion.div
                      initial={{ scale: 0 }} animate={{ scale: [0, 1.15, 1] }}
                      transition={{ delay: 0.15, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      className="relative w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-2xl shadow-emerald-200"
                    >
                      <Check className="w-12 h-12 text-white" strokeWidth={2.5} />
                    </motion.div>
                  </div>

                  {/* Text */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                  >
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">You're all set! 🎉</h2>
                    <p className="text-gray-400 text-sm max-w-xs mx-auto leading-relaxed">
                      Your WhatsApp keyword automation is configured. Set up keywords and your bot starts handling queries automatically.
                    </p>
                  </motion.div>

                  {/* Summary chips — honest copy */}
                  <motion.div
                    className="flex flex-wrap items-center justify-center gap-2"
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.4 }}
                  >
                    {[
                      { icon: Building2,    label: businessName || 'Your Business',   color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
                      { icon: Bot,          label: 'Keyword Bot Ready',               color: 'bg-blue-50 text-blue-700 border-blue-100'          },
                      { icon: Zap,          label: AUTOMATION_MODES.find(m => m.value === automationMode)?.label + ' Mode', color: 'bg-violet-50 text-violet-700 border-violet-100' },
                    ].map(({ icon: Icon, label, color }) => (
                      <div key={label} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${color}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                      </div>
                    ))}
                  </motion.div>

                  {/* Next steps preview */}
                  <motion.div
                    className="bg-gray-50 rounded-2xl p-5 text-left space-y-3 border border-gray-100"
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.4 }}
                  >
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">What to do next</p>
                    <div className="space-y-2.5">
                      {[
                        { icon: Zap,          label: 'Set up your first keyword reply',   desc: 'Goes to Automation → Keywords',   color: 'text-emerald-600 bg-emerald-50' },
                        { icon: MessageSquare, label: 'Add products to your catalog',     desc: 'Bot auto-replies with product info', color: 'text-blue-600 bg-blue-50'    },
                        { icon: Users,        label: 'Enable lead capture',               desc: 'Capture leads from conversations',  color: 'text-violet-600 bg-violet-50'  },
                        { icon: BarChart3,    label: 'Check your analytics',              desc: 'Track message volume & bot performance', color: 'text-teal-600 bg-teal-50' },
                      ].map(({ icon: Icon, label, desc, color }) => (
                        <div key={label} className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-800 leading-tight">{label}</p>
                            <p className="text-xs text-gray-400">{desc}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-300 ml-auto flex-shrink-0" />
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </motion.div>
              )}

            </AnimatePresence>

            {/* ── Navigation Buttons ─────────────────────────────────────────── */}
            <div className="flex gap-3 mt-8">
              {/* Back button */}
              {currentStep > 1 && currentStep < 4 && (
                <motion.button
                  type="button"
                  onClick={goBack}
                  whileTap={{ scale: 0.97 }}
                  data-testid="onboarding-back-button"
                  className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 hover:bg-gray-50 transition-all"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </motion.button>
              )}

              {/* Skip (step 2 only — WhatsApp is optional) */}
              {currentStep === 2 && (
                <button
                  type="button"
                  onClick={handleSkip}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all"
                >
                  Skip for now
                </button>
              )}

              {/* Next / Finish */}
              <motion.button
                type="button"
                onClick={handleNext}
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                data-testid="onboarding-next-button"
                className={`
                  flex-1 flex items-center justify-center gap-2
                  py-3 rounded-xl font-semibold text-sm text-white
                  shadow-lg shadow-emerald-200/60
                  disabled:opacity-60 disabled:cursor-not-allowed
                  transition-all duration-200
                  ${currentStep === 4
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700'
                    : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700'
                  }
                `}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Saving…
                  </>
                ) : currentStep === 4 ? (
                  <>
                    <Rocket className="w-4 h-4" />
                    Go to Dashboard
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </motion.button>
            </div>

            {/* Step counter */}
            <p className="text-center text-xs text-gray-300 mt-4">
              Step {currentStep} of {totalSteps}
            </p>

          </div>
        </div>

        {/* Below card note */}
        {currentStep < 4 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center text-xs text-gray-400 mt-5"
          >
            🔒 Your data is secure. We never share your business information.
          </motion.p>
        )}
      </motion.div>
    </div>
  );
}
